import { useTranslations } from "next-intl";
import { useCallback } from "react";
import { toast } from "sonner";
import { BaseError, UserRejectedRequestError } from "viem";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";

type StakeT = ReturnType<typeof useTranslations<"stake">>;

const TOAST_ID = "stake-01-tx";

// Known revert string → stake catalog key mapping
const REVERT_MAP: Record<string, string> = {
  "You should wait at least a year": "card.claimBlocked",
  "Pool is empty": "errors.poolEmpty",
  "DNM amount is not in correct range": "errors.dnmOutOfRange",
  "You are not owner of land": "modal.stakeError",
  "Invalid plan": "modal.stakeError",
  "stake is not active": "modal.stakeError",
};

function isUserRejection(err: unknown): boolean {
  // Direct match
  if (err instanceof UserRejectedRequestError) return true;

  // wagmi/viem wrap the inject-wallet reject in ContractFunctionExecutionError
  // with `.cause` pointing at UserRejectedRequestError. `.walk()` on BaseError
  // traverses the cause chain.
  if (err instanceof BaseError) {
    const hit = err.walk((e) => e instanceof UserRejectedRequestError);
    if (hit) return true;
  }

  // Text-based fallback for non-viem errors (raw EIP-1193 4001 etc).
  const msg =
    err instanceof Error
      ? err.message.toLowerCase()
      : typeof err === "string"
        ? err.toLowerCase()
        : "";
  return (
    msg.includes("user rejected") ||
    msg.includes("user denied") ||
    msg.includes("rejected the request") ||
    msg.includes("request rejected")
  );
}

function extractErrorMessage(
  err: unknown,
  t: StakeT,
  getMessage: (error: unknown) => string,
): string | null {
  if (!err) return null;

  if (isUserRejection(err)) {
    return t("errors.txCancelled");
  }

  if (err instanceof BaseError) {
    const msg = err.shortMessage ?? err.message;
    for (const [revert, key] of Object.entries(REVERT_MAP)) {
      if (msg.includes(revert)) return t(key);
    }
    return getMessage(err);
  }
  if (err instanceof Error) {
    for (const [revert, key] of Object.entries(REVERT_MAP)) {
      if (err.message.includes(revert)) return t(key);
    }
    return getMessage(err);
  }
  return null;
}

/**
 * BUG-19 single-emit wrapper over sonner toast.
 * Always calls toast.dismiss() before emitting to prevent duplicate toasts.
 */
export function useTxToasts() {
  const t = useTranslations("stake");
  const { getMessage } = useContractErrorHandler();

  const showSuccess = useCallback((msg: string) => {
    toast.dismiss();
    toast.success(msg, { id: TOAST_ID });
  }, []);

  const showError = useCallback(
    (err: unknown, fallback: string) => {
      toast.dismiss();
      const msg = extractErrorMessage(err, t, getMessage) ?? fallback;
      toast.error(msg, { id: TOAST_ID });
    },
    [t, getMessage],
  );

  const showInfo = useCallback((msg: string) => {
    toast.dismiss();
    toast.info(msg, { id: TOAST_ID });
  }, []);

  return { showSuccess, showError, showInfo };
}
